import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ProductLockup } from '../components/brand/Brand'
import { Icon, type IconName } from '../components/ui/Icon'
import { useAuth } from '../auth/AuthProvider'
import { useTenant } from '../tenancy/TenantProvider'
import type { CustomerModuleCode } from '../tenancy/service-access-api'
import { useLicenceDisplay } from '../tenancy/licence-display'
import { MyActionsShellControl } from '../governance/MyGovernanceTasks'

type NavItem = {
  label: string
  path: string
  icon: IconName
  tenantOnly?: boolean
  module?: CustomerModuleCode
}

export const customerNavigation: NavItem[] = [
  { label: 'Overview', path: '/', icon: 'overview' },
  { label: 'AI Register', path: '/ai-register', icon: 'register', tenantOnly: true, module: 'ai_register_product_library' },
  { label: 'AI Risks', path: '/ai-risks', icon: 'shield', tenantOnly: true, module: 'ai_register_product_library' },
  { label: 'Strategy & Roadmap', path: '/strategy', icon: 'strategy', tenantOnly: true, module: 'strategy_roadmap' },
  { label: 'Reports', path: '/reports', icon: 'reports', tenantOnly: true },
  { label: 'Administration', path: '/administration', icon: 'administration', tenantOnly: true },
]

export function AppShell() {
  const { profile, user, signOut } = useAuth()
  const { organisations, current, setCurrentOrganisationId, hasModule } = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const licence = useLicenceDisplay(current?.service ?? {
    status: 'provisioning',
    permitted: false,
    customerMessage: null,
    serviceCommencementAt: null,
    trialEndsAt: null,
    serviceEndsAt: null,
    versionNo: 0,
  })

  const handleSignOut = async () => {
    await signOut()
    void navigate('/auth/login', { replace: true })
  }

  const renderNavigation = () => customerNavigation.map((item) => {
    if (item.tenantOnly && !current) return null
    if (item.module && !hasModule(item.module)) return null

    return (
      <NavLink
        className={({ isActive }) => {
          const riskChildBelongsToRisks = item.path === '/ai-risks' && (location.pathname.startsWith('/ai-risks/') || location.pathname.startsWith('/risk-accept/'))
          const reportChildBelongsToReports = item.path === '/reports' && location.pathname.startsWith('/reports/')
          const strategyRoadmapBelongsToStrategy = item.path === '/strategy' && (location.pathname.startsWith('/strategy/') || location.pathname === '/roadmap' || location.pathname.startsWith('/roadmap/'))
          return `nav-item${isActive || riskChildBelongsToRisks || reportChildBelongsToReports || strategyRoadmapBelongsToStrategy ? ' active' : ''}`
        }}
        end={item.path === '/' || item.path === '/ai-risks'}
        key={item.label}
        to={item.path}
      >
        <span className="nav-glyph" aria-hidden="true"><Icon name={item.icon} /></span>
        <span>{item.label}</span>
      </NavLink>
    )
  })

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <ProductLockup inverse compact />
        </div>

        <div className="organisation-context">
          <span className="field-caption">Organisation</span>
          {organisations.length > 1 ? (
            <select
              aria-label="Current organisation"
              value={current?.organisation.id ?? ''}
              onChange={(event) => setCurrentOrganisationId(event.target.value)}
            >
              {organisations.map((access) => (
                <option key={access.organisation.id} value={access.organisation.id}>
                  {access.organisation.name}{access.service.permitted ? '' : ' — restricted'}
                </option>
              ))}
            </select>
          ) : (
            <strong className="organisation-name">{current?.organisation.name ?? 'No organisation selected'}</strong>
          )}
          {current ? (
            <span className={`licence-context ${licence.tone}`}>{licence.sidebarLabel}</span>
          ) : null}
          {current && !current.service.permitted ? (
            <span className="service-context restricted">
              {current.service.status === 'provisioning' ? 'Access is being prepared' : 'Access restricted'}
            </span>
          ) : null}
        </div>

        <details className="navigation-menu" open>
          <summary>
            <span>Navigate</span>
            <span aria-hidden="true">+</span>
          </summary>
          <nav className="primary-nav" aria-label="Swell AI">
            {renderNavigation()}
          </nav>
        </details>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <div className="topbar-spacer" aria-hidden="true" />
          <div className="topbar-actions">
            <MyActionsShellControl />
            <NavLink className="profile-link" to="/profile">
              <span className="avatar" aria-hidden="true">
                {(profile?.displayName ?? user?.email ?? 'U').slice(0, 1).toUpperCase()}
              </span>
              <span>
                <strong>{profile?.displayName ?? 'User'}</strong>
                <small>{user?.email}</small>
              </span>
            </NavLink>
            <button
              className="button text compact signout-button"
              type="button"
              onClick={() => { void handleSignOut() }}
            >
              <Icon name="signout" size={15} />
              <span>Sign out</span>
            </button>
          </div>
        </header>

        <main className="workspace-content"><Outlet /></main>
      </div>
    </div>
  )
}
